import { createActions } from 'redux-actions';
import { snakeCase, trimEnd } from 'lodash';
import { create, request } from '../../services';

const getKeyFromType = type => trimEnd(snakeCase(type), 's');

const getRequestBody = (type, lineItem) => {
  const body = {};
  const key = getKeyFromType(type);
  body[key] = lineItem;
  return body;
};

const {
  requestPhaseItemsRequest,
  requestPhaseItemsSuccess,
  requestPhaseItemsFailure,
} = createActions({
  REQUEST_PHASE_ITEMS_SUCCESS: [
    (lineItems, phaseID, type) => ({ line_items: lineItems, phaseID, type }),
    (newLineItem, phaseID, type, json) => json.meta || {},
  ],
}, 'REQUEST_PHASE_ITEMS_REQUEST', 'REQUEST_PHASE_ITEMS_FAILURE');

export function requestPhaseItems(phaseId, type) {
  let key = `${getKeyFromType(type)}s`;
  return (dispatch) => {
    dispatch(requestPhaseItemsRequest());
    return request(`phases/${phaseId}/${key}`)
      .then(({ json }) => {
        if (key === 'contractor_line_items') {
          key = 'labor_sheets';
          dispatch(requestPhaseItemsSuccess(json[`${key}`], phaseId, 'laborSheets', json));
        }
        dispatch(requestPhaseItemsSuccess(json[`${key}`], phaseId, type, json));
      })
      .catch(error => dispatch(requestPhaseItemsFailure(error)));
  };
}

const {
  requestPhaseLevelerItemsFailure,
  requestPhaseLevelerItemsRequest,
  requestPhaseLevelerItemsSuccess,
} = createActions({
  REQUEST_PHASE_LEVELER_ITEMS_SUCCESS: [
    (levelers, phaseID, type) => ({ levelers, phaseID, type }),
    (newLevelerItem, phaseID, type, json) => json.meta || {},
  ],
}, 'REQUEST_PHASE_LEVELER_ITEMS_REQUEST', 'REQUEST_PHASE_LEVELER_ITEMS_FAILURE');

export function requestPhaseLevelerItems(phaseId, type) {
  const key = `${getKeyFromType(type)}s`;
  return (dispatch) => {
    dispatch(requestPhaseLevelerItemsRequest());
    return request(`phases/${phaseId}/${key}`)
      .then(({ json }) => {
        dispatch(requestPhaseLevelerItemsSuccess(json[`${key}`], phaseId, type, json));
      })
      .catch(error => dispatch(requestPhaseLevelerItemsFailure(error)));
  };
}

export function loadPhaseItems(items, phaseId, type) {
  return (dispatch) => {
    dispatch(requestPhaseItemsSuccess(items, phaseId, type, {}));
  };
}

const {
  createPhaseItemsRequest,
  createPhaseItemsSuccess,
  createPhaseItemsFailure,
} = createActions({
  CREATE_PHASE_ITEMS_SUCCESS: [
    (newLineItem, phaseID, type) => ({ line_item: newLineItem, phaseID, type }),
    (newLineItem, phaseID, type, json) => json.meta || {},
  ],
  CREATE_PHASE_ITEMS_FAILURE: json => ({
    errors: json.error,
    line_item: json.lineItem,
    type: json.type,
  }),
}, 'CREATE_PHASE_ITEMS_REQUEST');

export function createPhaseItem(phaseId, lineItem, type) {
  return (dispatch) => {
    const key = getKeyFromType(type);
    dispatch(createPhaseItemsRequest());
    return request(`phases/${phaseId}/${key}s`, { method: 'POST', body: getRequestBody(type, lineItem) })
      .then(({ json }) =>
        dispatch(createPhaseItemsSuccess(json[key], phaseId, type, json)),
      )
      .catch(error =>
        dispatch(createPhaseItemsFailure({ error, lineItem, type })));
  };
}

const {
  createPhaseLevelerItemsRequest,
  createPhaseLevelerItemsSuccess,
  createPhaseLevelerItemsFailure,
} = createActions({
  CREATE_PHASE_LEVELER_ITEMS_SUCCESS: [
    (levelers, phaseID, type) => ({ levelers, phaseID, type }),
    (newLineItem, phaseID, type, json) => json.meta || {},
  ],
  CREATE_PHASE_LEVELER__ITEMS_FAILURE: json => ({
    errors: json.error,
    line_item: json.lineItem,
    type: json.type,
  }),
}, 'CREATE_PHASE_LEVELER_ITEMS_REQUEST');

export function createPhaseLevelerItem(phaseId, item, type) {
  const { bag_size, quantity } = item;
  const newItem = { bag_size, quantity };
  return (dispatch) => {
    const key = getKeyFromType(type);
    dispatch(createPhaseLevelerItemsRequest());
    return request(`phases/${phaseId}/${key}s`, { method: 'POST', body: getRequestBody(type, newItem) })
      .then(({ json }) =>
        dispatch(createPhaseLevelerItemsSuccess(json[key], phaseId, type, json)),
      )
      .catch(error =>
        dispatch(createPhaseLevelerItemsFailure({ error, newItem, type })));
  };
}

const {
  updatePhaseLevelerItemsSuccess,
  updatePhaseLevelerItemsRequest,
  updatePhaseLevelerItemsFailure,
} = createActions({
  UPDATE_PHASE_LEVELER_ITEMS_SUCCESS: [
    (levelers, phaseID, type) => ({ levelers, phaseID, type }),
    (newLevelerItem, phaseID, type, json) => json.meta || {},
  ],
  UPDATE_PHASE_LEVELER_ITEMS_FAILURE: json => ({
    errors: json.errors,
    levelers: json.levelers,
    type: json.type,
  }),
}, 'UPDATE_PHASE_LEVELER_ITEMS_REQUEST');

export function updatePhaseLevelerItem(phaseId, item, type) {
  const key = getKeyFromType(type);
  return (dispatch) => {
    dispatch(updatePhaseLevelerItemsRequest());
    return request(`phases/${phaseId}/${key}s/${item.id}`, { method: 'PUT', body: getRequestBody(type, item) })
      .then(({ json }) =>
        dispatch(updatePhaseLevelerItemsSuccess(json[key], phaseId, type, json)),
      )
      .catch(error => dispatch(updatePhaseLevelerItemsFailure({ error, item, type })));
  };
}

const {
  updatePhaseItemsSuccess,
  updatePhaseItemsRequest,
  updatePhaseItemsFailure,
} = createActions({
  UPDATE_PHASE_ITEMS_SUCCESS: [
    (updatedLineItem, phaseID, type) => ({ line_item: updatedLineItem, phaseID, type }),
    (updatedLineItem, phaseID, type, json) => json.meta || {},
  ],
  UPDATE_PHASE_ITEMS_FAILURE: json => ({
    errors: json.error,
    line_item: json.lineItem,
    type: json.type,
  }),
}, 'UPDATE_PHASE_ITEMS_REQUEST');

export function updatePhaseItem(phaseId, lineItem, type) {
  const key = getKeyFromType(type);
  return (dispatch) => {
    dispatch(updatePhaseItemsRequest());
    return request(`phases/${phaseId}/${key}s/${lineItem.id}`, { method: 'PUT', body: getRequestBody(type, lineItem) })
      .then(({ json }) =>
        dispatch(updatePhaseItemsSuccess(json[key], phaseId, type, json)),
      )
      .catch(error => dispatch(updatePhaseItemsFailure({ error, lineItem, type })));
  };
}

export function savePhaseItem(phaseId, lineItem, type) {
  return (dispatch) => {
    if (lineItem.id > 0) {
      return dispatch(updatePhaseItem(phaseId, lineItem, type));
    }
    return dispatch(createPhaseItem(phaseId, lineItem, type));
  };
}

export function savePhaseLevelerItem(phaseId, item, type) {
  return (dispatch) => {
    if (item.id > 0) {
      return dispatch(updatePhaseLevelerItem(phaseId, item, type));
    }
    return dispatch(createPhaseLevelerItem(phaseId, item, type));
  };
}

export function mergePurchaseOrderToOriginal(phaseId, purchaseOrderId) {
  return dispatch =>
    request(`phases/${phaseId}/purchase_orders/${purchaseOrderId}/merge_to_original`, { method: 'POST' })
      .then(() => Promise.all([
        dispatch(requestPhaseItems(phaseId, 'purchaseOrders')),
        dispatch(requestPhaseItems(phaseId, 'phaseLineItems')),
      ]));
}

const {
  deletePhaseItemsRequest,
  deletePhaseItemsSuccess,
  deletePhaseItemsFailure,
} = createActions({
  DELETE_PHASE_ITEMS_SUCCESS: [
    (lineItem, phaseID, type) => ({ line_item: lineItem, phaseID, type }),
    (lineItem, phaseID, type, json) => json.meta || {},
  ],
}, 'DELETE_PHASE_ITEMS_REQUEST', 'DELETE_PHASE_ITEMS_FAILURE');

export function deletePhaseItem(phaseId, lineItem, type) {
  return (dispatch) => {
    dispatch(deletePhaseItemsRequest());

    return request(`phases/${phaseId}/${getKeyFromType(type)}s/${lineItem.id}`, { method: 'DELETE' })
      .then(({ json }) =>
        dispatch(deletePhaseItemsSuccess(lineItem, phaseId, type, json)),
      )
      .catch(errors => dispatch(deletePhaseItemsFailure({ errors, line_item: lineItem, type })));
  };
}

const {
  phaseItemEventsRequest,
  phaseItemEventsSuccess,
  phaseItemEventsFailure,
} = createActions('PHASE_ITEM_EVENTS_SUCCESS', 'PHASE_ITEM_EVENTS_REQUEST', 'PHASE_ITEM_EVENTS_FAILURE');
export function requestPhaseItemEventList(type, lineItem) {
  const key = `${getKeyFromType(type)}s`;
  return (dispatch) => {
    dispatch(phaseItemEventsRequest({ type }));
    return request(`${key}/${lineItem.id}/events`)
      .then(({ json }) => (
        dispatch(phaseItemEventsSuccess({ line_item: lineItem, type, events: json.events }))
      ),
        (error) => {
          dispatch(phaseItemEventsFailure(error));
        });
  };
}

export function copyLaborItemsToLaborSheet(phaseId, laborSheetId) {
  return dispatch =>
    request(`phases/${phaseId}/labor_sheets/${laborSheetId}/copy_labor`, { method: 'POST' })
      .then(({ json }) => {
        dispatch(requestPhaseItemsSuccess(json.labor_sheets, phaseId, 'laborSheets', json));
        dispatch(requestPhaseItemsSuccess(json.labor_sheets, phaseId, 'contractorLineItems', json));
      },
        (error) => {
          dispatch(phaseItemEventsFailure(error));
        });
}

const {
  approveLaborSheetRequest,
  approveLaborSheetSuccess,
  approveLaborSheetFailure,
} = createActions('APPROVE_LABOR_SHEET_SUCCESS', 'APPROVE_LABOR_SHEET_REQUEST', 'APPROVE_LABOR_SHEET_FAILURE');
export function approveLaborSheet(phaseId, laborSheetId, document) {
  return (dispatch) => {
    dispatch(approveLaborSheetRequest());
    return request(`phases/${phaseId}/labor_sheets/${laborSheetId}/document`, { method: 'POST', body: { document } })
      .then(({ json }) => {
        dispatch(requestPhaseItemsSuccess(json.labor_sheets, phaseId, 'laborSheets', json));
        dispatch(requestPhaseItemsSuccess(json.labor_sheets, phaseId, 'contractorLineItems', json));
        dispatch(approveLaborSheetSuccess(json, phaseId));
      },
        (error) => {
          dispatch(approveLaborSheetFailure(error));
        });
  };
}

const {
  createExceptionRequest,
  createExceptionSuccess,
  createExceptionFailure,
} = createActions({
  CREATE_EXCEPTION_SUCCESS: json => json,
  CREATE_EXCEPTION_FAILURE: json => ({
    errors: json.error,
    po: json.po,
    line_items: json.line_items,
    type: 'exception',
  }),
}, 'CREATE_EXCEPTION_REQUEST');

export function createExceptionItem(exceptionData) {
  const type = 'exception';
  return (dispatch) => {
    dispatch(createExceptionRequest());
    return create('exception', exceptionData)
      .then(({ json }) =>
        dispatch(createExceptionSuccess(json)),
      )
      .catch(error =>
        dispatch(createExceptionFailure({ error, exceptionData, type })));
  };
}



// WEBPACK FOOTER //
// ./client/app/shared/actionCreators/phase_items.js